import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { AuthorizationMatrix } from 'app/core/store/session/authorization/authorization.interface';
import { getRecurrenceFormGroup, updateRecurrenceFormGroup } from 'app/modules/florder/recurrence/florder-recurrence.component';
import { ValidatorService } from 'app/core/services/validator.service';
import { CDOpeningHours } from 'app/core/store/contract-details/contract-details.interface';
import { OrderDetail } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { util } from 'app/core/util/util';
import { FlorderEditPlanningComponent } from 'app/modules/florder/florder-edit-planning/florder-edit-planning.component';
import { PlanningOrderPanelStatus } from 'app/modules/florder/florder-edit-container/florder-edit.interface';
import { ScrollService } from 'app/core/services/scroll.service';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DateRange } from '@dlw-int-mwi/eps-common';
import { appSettings } from '../../../appSettings';

@Component({
  selector: 'app-order-edit-planning',
  templateUrl: './order-edit-planning.component.html',
  styleUrls: ['../../florder/florder-edit-planning/florder-edit-planning.component.scss', './order-edit-planning.component.scss'],
})
export class OrderEditPlanningComponent extends FlorderEditPlanningComponent<PlanningOrderPanelStatus> implements OnChanges {
  orderContainsCombinations: boolean;
  labelWidth = appSettings.LABEL_WIDTH;

  singleRecurrenceValues: Definition[] = [];

  @Input() availablePickingDates: string[];
  @Input() orderDetail: OrderDetail;
  @Input() openingHours: CDOpeningHours[];
  @Input() authorization: AuthorizationMatrix;

  constructor(formBuilder: UntypedFormBuilder, validatorService: ValidatorService, scrollService: ScrollService, private cd: ChangeDetectorRef) {
    super(formBuilder, validatorService, scrollService);
    // push default empty
    const recurrenceMaxValue = 10;
    for (let index = 0; index < recurrenceMaxValue; index++) {
      const nr = index + 1;
      this.singleRecurrenceValues.push({ id: `${nr}`, name: `${nr}` });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (changes.orderDetail && this.orderDetail && this.editFlorderPlanningForm) {
      this.updateForm();
    }
  }

  createForm() {
    const dateInAvailableDatesValidator = (dateControl: UntypedFormControl) => {
      const value = dateControl.value;
      if (typeof value === 'string' && value !== '') {
        if (this.availablePickingDates.indexOf(value) < 0) {
          return { dateNotAvailable: true };
        }
      }
      return null;
    };
    this.orderContainsCombinations = this.orderDetail && this.orderDetail.materials && !!this.orderDetail.materials.find(m => m.type === 'combination');
    const loadingDateValidators =
      this.panelStatus.createTemplate || this.panelStatus.editTemplate ? null : [Validators.required, this.validatorService.datePatternValidator, dateInAvailableDatesValidator];
    const controlsConfig = {
      orderDate: [this.orderDetail.planning.orderDate || undefined],
      loadingDate: [this.orderDetail.planning.loadingDate || undefined, loadingDateValidators],
      exchangePallets: [this.orderDetail.planning.maxAmountOfPalletExchangeAvailable === 0 ? false : this.orderDetail.planning.exchangePallets],
      numberOfExchangePallets: [
        '' + ((this.orderContainsCombinations && (this.orderDetail.planning.numberOfExchangePallets || this.orderDetail.planning.maxAmountOfPalletExchangeAvailable)) || 0),
      ],
      createTemplate: [this.orderDetail.createTemplate || this.panelStatus.createTemplate || this.panelStatus.editTemplate, Validators.required],
      templateName: [this.orderDetail.templateName],
      recurrence: getRecurrenceFormGroup(this.orderDetail, this.formBuilder, this.validatorService),
      singleRecurrence: ['1', [Validators.required]],
      definitiveDate: [this.orderDetail.planning.definitiveDate, null, null],
    };
    this.editFlorderPlanningForm = this.formBuilder.group(controlsConfig);

    const maxValidator = (c: AbstractControl): ValidationErrors | null => {
      return Validators.max(this.orderDetail.planning.maxAmountOfPalletExchangeAvailable)(c); // This way, the actual value of this field is used instead of the value when the validator is created
    };
    util.conditionalValidation(this.editFlorderPlanningForm, 'numberOfExchangePallets', [util.createCondition('exchangePallets')], [Validators.required, maxValidator, Validators.min(1)]);
    util.conditionalValidation(this.editFlorderPlanningForm, 'templateName', [util.createCondition('createTemplate')], [Validators.required]);

    this.formValidChanged.emit(this.editFlorderPlanningForm.valid);
    this.editFlorderPlanningForm.statusChanges
      .pipe(
        map(data => this.editFlorderPlanningForm.valid),
        distinctUntilChanged()
      )
      .subscribe(valid => {
        this.formValidChanged.emit(valid);
      });
  }

  updateForm() {
    this.orderContainsCombinations = this.orderDetail && this.orderDetail.materials && !!this.orderDetail.materials.find(m => m.type === 'combination');
    this.editFlorderPlanningForm.patchValue({
      orderDate: this.orderDetail.planning.orderDate || undefined,
      loadingDate: this.orderDetail.planning.loadingDate || undefined,
      exchangePallets: this.orderDetail.planning.exchangePallets,
      numberOfExchangePallets: this.orderDetail.planning.numberOfExchangePallets || this.orderDetail.planning.maxAmountOfPalletExchangeAvailable,
      createTemplate: this.orderDetail.createTemplate || this.panelStatus.createTemplate || this.panelStatus.editTemplate,
      templateName: this.orderDetail.templateName,
    });
    const recurrenceControl = this.editFlorderPlanningForm.get('recurrence') as UntypedFormGroup;
    updateRecurrenceFormGroup(recurrenceControl, this.orderDetail);
  }

  getOpeningHoursText(): string {
    const date = this.editFlorderPlanningForm && util.parseDateString(this.editFlorderPlanningForm.value.loadingDate);
    if (date && this.openingHours) {
      const selectedDate = new Date(date.year, date.month - 1, date.day);
      const openingHour = this.openingHours.find(oh => oh.weekdayNr === selectedDate.getDay());
      return (
        openingHour &&
        openingHour.slots.reduce((t, slot) => {
          return (t.length ? t + ' / ' : '') + `${slot.from} - ${slot.to}`;
        }, '')
      );
    }
    return '';
  }

  doNextClicked() {
    if (this.isValidForm()) {
      this.nextClicked.emit(this.editFlorderPlanningForm.value);
    }
  }

  /**
   * TODO store it as property
   */
  get validLoadingDates(): DateRange[] {
    const ranges = [];
    this.availablePickingDates.forEach(date => {
      ranges.push({ startDate: date, endDate: date });
    })
    return ranges;
  }
}
